import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { resolveSeo, SITE_NAME } from "@/lib/seoRoutes";

/**
 * Client-side metadata sync.
 *
 * The route tables live in `@/lib/seoRoutes` because the edge middleware at the
 * repo root imports the same module to stamp this metadata into the HTML before
 * it is served. This component keeps the tags correct as the user navigates
 * within the SPA; the middleware is what crawlers and link-preview bots see.
 * Do not fork the values — change them in seoRoutes.js.
 */

function ensureMeta(attr, value) {
  const selector = attr === "property" ? `meta[property="${value}"]` : `meta[name="${value}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  return el;
}

function ensureLink(rel) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  return el;
}

function ensureScript(id) {
  let el = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  return el;
}

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const seo = resolveSeo(host, pathname);

    // Unknown host (a merchant's own storefront domain, a preview URL) — leave
    // whatever the storefront set in place rather than stamping Ordrs branding
    // onto someone else's site.
    if (!seo) return;

    document.title = seo.title;

    ensureMeta("name", "description").setAttribute("content", seo.description);
    ensureMeta("name", "keywords").setAttribute("content", seo.keywords);
    ensureMeta("name", "robots").setAttribute("content", seo.noIndex ? "noindex, nofollow" : "index, follow");
    ensureMeta("property", "og:site_name").setAttribute("content", SITE_NAME);
    ensureMeta("property", "og:type").setAttribute("content", "website");
    ensureMeta("property", "og:title").setAttribute("content", seo.title);
    ensureMeta("property", "og:description").setAttribute("content", seo.description);
    ensureMeta("property", "og:url").setAttribute("content", seo.canonicalUrl);
    ensureMeta("property", "og:image").setAttribute("content", seo.image);
    ensureMeta("name", "twitter:card").setAttribute("content", "summary_large_image");
    ensureMeta("name", "twitter:title").setAttribute("content", seo.title);
    ensureMeta("name", "twitter:description").setAttribute("content", seo.description);
    ensureMeta("name", "twitter:image").setAttribute("content", seo.image);
    ensureLink("canonical").setAttribute("href", seo.canonicalUrl);

    ensureScript("route-jsonld").textContent = JSON.stringify(seo.jsonLd);
  }, [pathname]);

  return null;
}
